import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Form, Modal, Button, Row, Col, Badge, Alert, Spinner } from "react-bootstrap";
import { fetchGlobalSettings, updateGlobalSettings } from "../../actions/admin";
import { gethandleTransactionsStatusCHeck, triggerTrackingCron, fetchDtdcConfigs } from "../../actions/product";
import FullPageSpinner from "../layout/FullPageSpinner";
import DtdcConfigManager from "./DtdcConfigManager";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { ROLES } from "../../utils/constants";
import {
  Sliders,
  ShieldCheck,
  Truck,
  Package,
  ShoppingBag,
  Eye,
  EyeOff,
  Clock,
  Plus,
  Trash2,
  RotateCcw,
  AlertTriangle,
  CheckCircle,
  CreditCard,
  Activity,
  Layers,
  ChevronRight,
  Calculator,
  Lock,
  Sparkles,
  Download,
  Copy,
  Check,
  Zap,
  UserCheck,
} from "lucide-react";
import "./Settings.css";

const DEFAULT_WEIGHT_SLABS = [
  { minWeight: 0, maxWeight: 1.0, cost: 105 },
  { minWeight: 1.0, maxWeight: 2.0, cost: 185 },
  { minWeight: 2.0, maxWeight: 3.0, cost: 265 },
  { minWeight: 3.0, maxWeight: 4.0, cost: 345 },
];

const FINE_WEIGHT_SLABS = [
  { minWeight: 0, maxWeight: 1.0, cost: 105 },
  { minWeight: 1.0, maxWeight: 1.5, cost: 145 },
  { minWeight: 1.5, maxWeight: 2.0, cost: 185 },
  { minWeight: 2.0, maxWeight: 3.0, cost: 265 },
  { minWeight: 3.0, maxWeight: 4.0, cost: 345 },
];

const FLAT_WEIGHT_SLABS = [
  { minWeight: 0, maxWeight: 5.0, cost: 120 },
];

const Settings = () => {
  const { user } = useSelector((state) => state.auth);

  const initialSettingsState = {
    enableIndividualProducts: true,
    enableBulkProducts: true,
    enablePurchasing: true,
    individualProductEnableAfterDays: 0,
    individualProductVisibility: "ALL",
    allowedIndividualProductUsids: "",
    enableHomeDelivery: true,
    homeDeliveryPrice: 0,
    additionalBundlePrice: 0,
    perKgRate: 80,
    maxWeight:4,
    weightSlabs: DEFAULT_WEIGHT_SLABS,
  };

  const [settings, setSettings] = useState(initialSettingsState);
  const [savedSettings, setSavedSettings] = useState(initialSettingsState);
  const [loading, setLoading] = useState(false);
  const [syncingTracking, setSyncingTracking] = useState(false);
  const [syncingPayments, setSyncingPayments] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [showDtdcManager, setShowDtdcManager] = useState(false);
  const [dtdcConfigs, setDtdcConfigs] = useState([]);

  // Security verification modal
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [securityCode, setSecurityCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Live calculator test weight
  const [calcWeight, setCalcWeight] = useState(1.5);
  const [copiedConfig, setCopiedConfig] = useState(false);

  const isValidSlabs = (slabs) => {
    return (
      Array.isArray(slabs) &&
      slabs.length > 0 &&
      typeof slabs[0] === "object" &&
      !Array.isArray(slabs[0]) &&
      slabs.some((s) => Number(s?.cost) > 0)
    );
  };

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchGlobalSettings();
      if (response?.success && response?.settings) {
        const loaded = {
          enableIndividualProducts: response.settings.enableIndividualProducts ?? true,
          enableBulkProducts: response.settings.enableBulkProducts ?? true,
          enablePurchasing: response.settings.enablePurchasing ?? true,
          individualProductEnableAfterDays: response.settings.individualProductEnableAfterDays ?? 0,
          individualProductVisibility: response.settings.individualProductVisibility ?? "ALL",
          allowedIndividualProductUsids: Array.isArray(response.settings.allowedIndividualProductUsids)
            ? response.settings.allowedIndividualProductUsids.join(", ")
            : (response.settings.allowedIndividualProductUsids ?? ""),
          enableHomeDelivery: response.settings.enableHomeDelivery ?? false,
          homeDeliveryPrice: response.settings.homeDeliveryPrice ?? 0,
          additionalBundlePrice: response.settings.additionalBundlePrice ?? 0,
          perKgRate: response.settings.perKgRate ?? 80,
          maxWeight: response.settings.maxWeight ?? 4,
          weightSlabs: isValidSlabs(response.settings.weightSlabs)
            ? response.settings.weightSlabs
            : DEFAULT_WEIGHT_SLABS,
        };
        setSettings(loaded);
        setSavedSettings(loaded);
      }
      try {
        const dtdcData = await fetchDtdcConfigs();
        if (Array.isArray(dtdcData)) {
          setDtdcConfigs(dtdcData);
        }
      } catch (e) {
        console.error("Failed to fetch DTDC configs:", e);
      }
    } catch (error) {
      toast.error("Failed to load global settings", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Detect dirty state (unsaved changes)
  const isDirty = useMemo(() => {
    return JSON.stringify(settings) !== JSON.stringify(savedSettings);
  }, [settings, savedSettings]);

  // Keyboard shortcut: Ctrl + S / Cmd + S to save
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (isDirty) {
          setShowSecurityModal(true);
        } else {
          toast.info("All settings are already up to date.");
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDirty]);

  const handleToggle = (settingKey) => {
    setSettings((prev) => ({
      ...prev,
      [settingKey]: !prev[settingKey],
    }));
  };

  const updateSetting = (key, value) => {
    setSettings((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "individualProductVisibility" && value !== "CURRENT_YEAR_ORDERED") {
        next.individualProductEnableAfterDays = 0;
      }
      return next;
    });
  };

  const handleSlabChange = (index, field, value) => {
    setSettings((prev) => {
      const updatedSlabs = [...(prev.weightSlabs || [])];
      updatedSlabs[index] = {
        ...updatedSlabs[index],
        [field]: value,
      };
      return { ...prev, weightSlabs: updatedSlabs };
    });
  };

  const handleAddSlab = () => {
    setSettings((prev) => {
      const slabs = prev.weightSlabs || [];
      const maxAllowedWeight = Number(settings.maxWeight) || 0;

      const lastSlab = slabs[slabs.length - 1];

      const newMin = lastSlab
        ? Number((Number(lastSlab.maxWeight) + 0.1).toFixed(1))
        : 0;

      const newMax = lastSlab
        ? Number((Number(lastSlab.maxWeight) + 1.0).toFixed(1))
        : 1.0;

      // Don't add if it exceeds configured max weight
      if (maxAllowedWeight > 0 && newMax > maxAllowedWeight) {
        toast.warn(`Maximum allowed weight is ${maxAllowedWeight} kg.`);
        return prev;
      }

      const newCost = lastSlab
        ? Number(lastSlab.cost) + 80
        : 105;

      return {
        ...prev,
        weightSlabs: [
          ...slabs,
          {
            minWeight: newMin,
            maxWeight: newMax,
            cost: newCost,
          },
        ],
      };
    });
  };

  const handleRemoveSlab = (index) => {
    if ((settings.weightSlabs || []).length <= 1) {
      toast.warn("At least one weight slab is required.");
      return;
    }
    setSettings((prev) => {
      const updatedSlabs = (prev.weightSlabs || []).filter((_, i) => i !== index);
      return { ...prev, weightSlabs: updatedSlabs };
    });
  };

  const handleApplyPreset = (presetName) => {
    if (presetName === "fine") {
      setSettings((prev) => ({ ...prev, weightSlabs: FINE_WEIGHT_SLABS }));
      toast.info("Applied 0.5kg Fine-Grained Weight Slabs");
    } else if (presetName === "flat") {
      setSettings((prev) => ({ ...prev, weightSlabs: FLAT_WEIGHT_SLABS }));
      toast.info("Applied Flat-Rate Delivery Tier");
    } else {
      setSettings((prev) => ({ ...prev, weightSlabs: DEFAULT_WEIGHT_SLABS, perKgRate: 80 }));
      toast.info("Restored Standard 4-Tier Courier Slabs");
    }
  };

  const handleResetChanges = () => {
    setSettings(savedSettings);
    toast.info("Changes reset to current saved settings.");
  };

  const handleSave = () => {
    setShowSecurityModal(true);
  };

  const handleSecurityCodeSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!securityCode.trim()) {
      toast.error("Security code is required to authorize modifications", { position: "top-right" });
      return;
    }

    setLoading(true);
    try {
      const sub100Slab = (settings.weightSlabs || []).find((s) => Number(s.cost) < 100);
      if (sub100Slab) {
        toast.error(`Delivery charge cannot be less than ₹100. Slab with cost ₹${sub100Slab.cost} is not allowed.`, { position: "top-right" });
        setLoading(false);
        return;
      }

      const sanitizedSlabs = (settings.weightSlabs || []).map((s) => ({
        minWeight: Number(s.minWeight) || 0,
        maxWeight: Number(s.maxWeight) || 0,
        cost: Number(s.cost) || 0,
      }));

      const parsedAllowedUsids = typeof settings.allowedIndividualProductUsids === "string"
        ? settings.allowedIndividualProductUsids
            .split(/[\n,]+/)
            .map((u) => u.trim())
            .filter(Boolean)
        : (settings.allowedIndividualProductUsids || []);

      const response = await updateGlobalSettings({
        ...settings,
        allowedIndividualProductUsids: parsedAllowedUsids,
        weightSlabs: sanitizedSlabs,
        securityCode,
      });

      if (response && response.success) {
        setShowSecurityModal(false);
        setSecurityCode("");
        setSavedSettings({
          ...settings,
          allowedIndividualProductUsids: settings.allowedIndividualProductUsids,
          weightSlabs: sanitizedSlabs,
        });
      }
    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error("Failed to update settings. Please check your security code.", {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerCron = async () => {
    if (!window.confirm("Are you sure you want to trigger DTDC tracking update? This will query latest delivery status for all active orders.")) return;

    setSyncingTracking(true);
    try {
      const response = await triggerTrackingCron();
      if (response) {
        toast.success("DTDC tracking update triggered successfully", { position: "top-right" });
      }
    } catch (error) {
      toast.error("Failed to trigger tracking update", { position: "top-right" });
    } finally {
      setSyncingTracking(false);
    }
  };

  const handleTransactionsStatusCheck = async () => {
    if (!window.confirm("Are you sure you want to reconcile payment transaction statuses with the payment gateway?")) return;

    setSyncingPayments(true);
    try {
      const response = await gethandleTransactionsStatusCHeck();
      if (response) {
        toast.success("Payment status reconciliation completed successfully", { position: "top-right" });
      }
    } catch (error) {
      toast.error("Failed to trigger payment reconciliation", { position: "top-right" });
    } finally {
      setSyncingPayments(false);
    }
  };

  // Export JSON Backup
  const handleExportJson = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(settings, null, 2));
    const downloadAnchor = document.createElement("a");
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute("download", `GlobalSettings_Backup_${new Date().toISOString().slice(0, 10)}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
    toast.success("Settings backup downloaded as JSON");
  };

  // Copy Config to Clipboard
  const handleCopyConfig = () => {
    navigator.clipboard.writeText(JSON.stringify(settings, null, 2));
    setCopiedConfig(true);
    toast.success("Settings configuration copied to clipboard");
    setTimeout(() => setCopiedConfig(false), 2000);
  };

  // Slabs Health & Continuity Checker
  const slabsValidation = useMemo(() => {
    const slabs = settings.weightSlabs || [];
    let hasError = false;
    let message = "All weight tiers are continuous and valid.";

    for (let i = 0; i < slabs.length; i++) {
      const s = slabs[i];
      if (Number(s.minWeight) >= Number(s.maxWeight) && Number(s.maxWeight) !== 0) {
        hasError = true;
        message = `Slab #${i + 1} has Min Weight (≥ Max Weight).`;
        break;
      }
      if (Number(s.cost) < 100) {
        hasError = true;
        message = `Slab #${i + 1} has cost ₹${s.cost}. Minimum delivery charge must be at least ₹100.`;
        break;
      }
      if (i > 0) {
        const prev = slabs[i - 1];
        if (Number(s.minWeight) < Number(prev.maxWeight)) {
          hasError = true;
          message = `Overlap detected between Slab #${i} and Slab #${i + 1}.`;
          break;
        }
      }
    }

    return { isValid: !hasError, message };
  }, [settings.weightSlabs]);

  // Live Shipping Cost Calculation Preview
  const liveShippingCost = useMemo(() => {
    const w = Number(calcWeight) || 0;
    if (w <= 0) return 0;
    const slabs = [...(settings.weightSlabs || [])]
      .map((s) => ({
        minWeight: Number(s.minWeight) || 0,
        maxWeight: Number(s.maxWeight) || 0,
        cost: Number(s.cost) || 0,
      }))
      .filter((s) => s.maxWeight > 0)
      .sort((a, b) => a.maxWeight - b.maxWeight);

    if (slabs.length > 0) {
      const matchedSlab = slabs.find((s) => w <= s.maxWeight);
      if (matchedSlab) {
        return Number(matchedSlab.cost) || 0;
      }
      const topSlab = slabs[slabs.length - 1];
      const baseCost = Number(topSlab.cost) || 0;
      const rate = Number(settings.perKgRate) || 80;
      const extraWeight = Math.max(0, w - topSlab.maxWeight);
      return baseCost + Math.ceil(extraWeight) * rate;
    }
    return 0;
  }, [calcWeight, settings.weightSlabs, settings.perKgRate]);

  return (
    <div className="settings-container">
      {loading && <FullPageSpinner loading={loading} />}

      {/* Breadcrumb Navigation */}
      <div className="settings-breadcrumb">
        <span className="settings-breadcrumb-item">
          <Sliders size={15} /> Administration
        </span>
        <ChevronRight size={14} className="text-muted" />
        <span className="settings-breadcrumb-item active">Global System Settings</span>
      </div>

      {/* Header Card */}
      <div className="settings-header-card">
        <div className="settings-header-title-group">
          <div className="settings-header-icon-box">
            <Sliders size={24} />
          </div>
          <div>
            <h1 className="settings-header-title">System & Global Settings</h1>
            <p className="settings-header-subtitle">
              Manage store purchasing rules, individual product visibility, delivery pricing, and courier weight slabs.
            </p>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Button
            variant="outline-secondary"
            size="sm"
            className="d-inline-flex align-items-center gap-1 px-3 py-2 fw-semibold"
            onClick={handleCopyConfig}
            title="Copy settings JSON"
          >
            {copiedConfig ? <Check size={15} className="text-success" /> : <Copy size={15} />}
            {copiedConfig ? "Copied" : "Copy Config"}
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            className="d-inline-flex align-items-center gap-1 px-3 py-2 fw-semibold"
            onClick={handleExportJson}
            title="Download JSON backup"
          >
            <Download size={15} /> Export JSON
          </Button>

          {isDirty && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="d-inline-flex align-items-center gap-1 px-3 py-2 fw-semibold"
              onClick={handleResetChanges}
            >
              <RotateCcw size={15} /> Reset Changes
            </Button>
          )}

          <Button
            variant={isDirty ? "primary" : "outline-primary"}
            size="sm"
            className="d-inline-flex align-items-center gap-2 px-4 py-2 fw-bold shadow-sm"
            onClick={handleSave}
          >
            <ShieldCheck size={18} />
            {isDirty ? "Save Changes" : "Save Settings"}
            {isDirty && (
              <Badge bg="warning" text="dark" className="ms-1 px-2 py-0">
                Unsaved
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Live Status Strip */}
      <div className="settings-status-strip">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <span className="small text-muted fw-bold text-uppercase">Live State:</span>
          <div className={`status-indicator-badge ${settings.enablePurchasing ? "status-on" : "status-off"}`}>
            <CreditCard size={13} />
            Purchases: {settings.enablePurchasing ? "Enabled" : "Locked"}
          </div>
          <div className={`status-indicator-badge ${settings.enableHomeDelivery ? "status-on" : "status-off"}`}>
            <Truck size={13} />
            Delivery: {settings.enableHomeDelivery ? `Active (₹${settings.homeDeliveryPrice})` : "Campus Pickup"}
          </div>
          <div className="status-indicator-badge status-neutral">
            <Eye size={13} />
            Catalog: {settings.individualProductVisibility === "ALL" ? "Public (All Parents)" : `Restricted (${settings.individualProductEnableAfterDays}d Grace)`}
          </div>
        </div>

        <div className="small text-muted d-none d-md-block">
          <kbd className="bg-light text-dark border px-2 py-1">Ctrl + S</kbd> to quick save
        </div>
      </div>

      {/* Unsaved Changes Banner */}
      {isDirty && (
        <Alert variant="warning" className="d-flex align-items-center justify-content-between py-2 px-3 mb-4 rounded-3 border-warning">
          <div className="d-flex align-items-center gap-2">
            <Sparkles size={18} className="text-warning" />
            <span className="fw-semibold small">
              You have unsaved changes in settings. Don't forget to click <strong>"Save Changes"</strong> to verify and apply them.
            </span>
          </div>
          <Button variant="warning" size="sm" className="fw-bold px-3 py-1 text-dark" onClick={handleSave}>
            Save Now
          </Button>
        </Alert>
      )}

      {/* Section Filter Tabs */}
      <div className="settings-nav-tabs">
        <button
          type="button"
          className={`settings-nav-tab-btn ${activeTab === "all" ? "active" : ""}`}
          onClick={() => setActiveTab("all")}
        >
          <Zap size={14} /> All Settings
        </button>
        <button
          type="button"
          className={`settings-nav-tab-btn ${activeTab === "store" ? "active" : ""}`}
          onClick={() => setActiveTab("store")}
        >
          <ShoppingBag size={14} /> Storefront & Purchases
        </button>
        <button
          type="button"
          className={`settings-nav-tab-btn ${activeTab === "visibility" ? "active" : ""}`}
          onClick={() => setActiveTab("visibility")}
        >
          <Eye size={14} /> Visibility Rules
        </button>
        <button
          type="button"
          className={`settings-nav-tab-btn ${activeTab === "shipping" ? "active" : ""}`}
          onClick={() => setActiveTab("shipping")}
        >
          <Truck size={14} /> Home Delivery & Slabs
        </button>
        <button
          type="button"
          className={`settings-nav-tab-btn ${activeTab === "dtdc" ? "active" : ""}`}
          onClick={() => setActiveTab("dtdc")}
        >
          <Package size={14} /> DTDC Customer Codes
        </button>
        {user?.role === ROLES.SUPER_ADMIN && (
          <button
            type="button"
            className={`settings-nav-tab-btn ${activeTab === "maint" ? "active" : ""}`}
            onClick={() => setActiveTab("maint")}
          >
            <Activity size={14} /> System Maintenance
          </button>
        )}
      </div>

      {/* Section 1: Store & Purchasing Rules */}
      {(activeTab === "all" || activeTab === "store") && (
        <div className="settings-section-card">
          <div className="settings-section-header">
            <h5 className="settings-section-title">
              <ShoppingBag size={20} className="text-primary" /> Store & Purchasing Configuration
            </h5>
            <Badge bg="light" text="dark" className="border px-2 py-1">
              Storefront
            </Badge>
          </div>
          <div className="settings-section-body">
            <Row className="g-3">
              <Col xs={12} md={4}>
                <div className={`setting-tile ${settings.enablePurchasing ? "is-active" : ""}`}>
                  <div className="d-flex align-items-start justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="setting-tile-icon icon-red">
                        <CreditCard size={20} />
                      </div>
                      <div>
                        <h6 className="fw-bold text-dark mb-1">Purchasing System</h6>
                        <span className="small text-muted">Checkout & Payment Gateway</span>
                      </div>
                    </div>
                    <Form.Check
                      type="switch"
                      id="enable-purchasing-switch"
                      checked={settings.enablePurchasing}
                      onChange={() => handleToggle("enablePurchasing")}
                    />
                  </div>
                  <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                    <span className="small text-muted">System State:</span>
                    <Badge bg={settings.enablePurchasing ? "success" : "danger"} className="px-2 py-1">
                      {settings.enablePurchasing ? "Active (Purchases Allowed)" : "Locked (Store Disabled)"}
                    </Badge>
                  </div>
                </div>
              </Col>

              <Col xs={12} md={4}>
                <div className={`setting-tile ${settings.enableIndividualProducts ? "is-active" : ""}`}>
                  <div className="d-flex align-items-start justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="setting-tile-icon icon-blue">
                        <Package size={20} />
                      </div>
                      <div>
                        <h6 className="fw-bold text-dark mb-1">Individual Products</h6>
                        <span className="small text-muted">Single uniform items list</span>
                      </div>
                    </div>
                    <Form.Check
                      type="switch"
                      id="enable-individual-switch"
                      checked={settings.enableIndividualProducts}
                      onChange={() => handleToggle("enableIndividualProducts")}
                    />
                  </div>
                  <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                    <span className="small text-muted">Visibility:</span>
                    <Badge bg={settings.enableIndividualProducts ? "primary" : "secondary"} className="px-2 py-1">
                      {settings.enableIndividualProducts ? "Available" : "Hidden"}
                    </Badge>
                  </div>
                </div>
              </Col>

              <Col xs={12} md={4}>
                <div className={`setting-tile ${settings.enableBulkProducts ? "is-active" : ""}`}>
                  <div className="d-flex align-items-start justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="setting-tile-icon icon-purple">
                        <Layers size={20} />
                      </div>
                      <div>
                        <h6 className="fw-bold text-dark mb-1">Uniform Bundles</h6>
                        <span className="small text-muted">Mandatory & standard sets</span>
                      </div>
                    </div>
                    <Form.Check
                      type="switch"
                      id="enable-bulk-switch"
                      checked={settings.enableBulkProducts}
                      onChange={() => handleToggle("enableBulkProducts")}
                    />
                  </div>
                  <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                    <span className="small text-muted">Visibility:</span>
                    <Badge bg={settings.enableBulkProducts ? "primary" : "secondary"} className="px-2 py-1">
                      {settings.enableBulkProducts ? "Available" : "Hidden"}
                    </Badge>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      )}

      {/* Section 2: Visibility & Access Rules */}
      {(activeTab === "all" || activeTab === "visibility") && (
        <div className="settings-section-card">
          <div className="settings-section-header">
            <h5 className="settings-section-title">
              <Eye size={20} className="text-primary" /> Individual Products Visibility Rules
            </h5>
            <Badge bg="light" text="dark" className="border px-2 py-1">
              Access Control
            </Badge>
          </div>
          <div className="settings-section-body">
            <Row className="g-3 align-items-center">
              <Col xs={12} md={6}>
                <div className="p-3 bg-light rounded-3 border">
                  <Form.Label className="fw-bold text-dark small mb-1">Who Can View Individual Products?</Form.Label>
                  <p className="text-muted small mb-2">
                    Restrict loose uniform items only to parents who have placed their mandatory uniform bundle order this academic year.
                  </p>
                    <Form.Select
                    value={settings.individualProductVisibility}
                    onChange={(e) => updateSetting("individualProductVisibility", e.target.value)}
                    className="fw-semibold"
                  >
                    <option value="ALL">All Parents & Users (Public Catalog)</option>
                    <option value="CURRENT_YEAR_ORDERED">Current Year Bundle Buyers Only (Restricted)</option>
                    <option value="SPECIFIC_USIDS">Whitelisted Specific USIDs Only (Pre-Live / Testing)</option>
                  </Form.Select>
                </div>
              </Col>

              {settings.individualProductVisibility === "CURRENT_YEAR_ORDERED" && (
                <Col xs={12} md={6}>
                  <div className="p-3 bg-warning bg-opacity-10 border border-warning rounded-3">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <Clock size={16} className="text-warning" />
                      <Form.Label className="fw-bold text-dark small mb-0">Grace Period / Cooldown (Days)</Form.Label>
                    </div>
                    <p className="text-muted small mb-2">
                      Number of days to wait after bundle order placement before individual items become unlocked for the parent.
                    </p>
                    <div className="input-group input-group-sm" style={{ maxWidth: "200px" }}>
                      <Form.Control
                        type="number"
                        min={0}
                        className="fw-bold text-center"
                        value={settings.individualProductEnableAfterDays}
                        onChange={(e) => updateSetting("individualProductEnableAfterDays", Math.max(0, parseInt(e.target.value, 10) || 0))}
                      />
                      <span className="input-group-text bg-white fw-semibold text-muted">Days</span>
                    </div>
                  </div>
                </Col>
              )}

              <Col xs={12}>
                <div className="p-3 bg-primary bg-opacity-10 border border-primary border-opacity-25 rounded-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <UserCheck size={18} className="text-primary" />
                      <Form.Label className="fw-bold text-dark small mb-0">
                        Allowed Pre-Live / Testing USIDs (Whitelist)
                      </Form.Label>
                    </div>
                    {settings.allowedIndividualProductUsids && (
                      <Badge bg="primary" pill>
                        {
                          typeof settings.allowedIndividualProductUsids === "string"
                            ? settings.allowedIndividualProductUsids.split(/[\n,]+/).map(u => u.trim()).filter(Boolean).length
                            : (settings.allowedIndividualProductUsids || []).length
                        } USID(s) configured
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted small mb-2">
                    Enter comma-separated or newline-separated Student / Parent USIDs (e.g. <code>202401, 202402</code>). 
                    Users matching these USIDs will be granted access to browse and purchase single products prior to going live.
                  </p>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="e.g. 2024001, 2024002, GAUD0091"
                    className="font-monospace small"
                    value={settings.allowedIndividualProductUsids || ""}
                    onChange={(e) => updateSetting("allowedIndividualProductUsids", e.target.value)}
                  />
                </div>
              </Col>
            </Row>
          </div>
        </div>
      )}

      {/* Section 3: Shipping & Logistics */}
      {(activeTab === "all" || activeTab === "shipping") && (
        <div className="settings-section-card">
          <div className="settings-section-header">
            <h5 className="settings-section-title">
              <Truck size={20} className="text-primary" /> Home Delivery & Courier Shipping
            </h5>
            <Badge bg="light" text="dark" className="border px-2 py-1">
              Logistics
            </Badge>
          </div>
          <div className="settings-section-body">
            <Row className="g-3 mb-4">
              <Col xs={12}>
                <div className={`setting-tile ${settings.enableHomeDelivery ? "is-active" : ""}`}>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                      <div className="setting-tile-icon icon-green">
                        <Truck size={20} />
                      </div>
                      <div>
                        <h6 className="fw-bold text-dark mb-1">Enable Home Delivery Service</h6>
                        <p className="text-muted small mb-0">
                          Allow parents to choose doorstep courier delivery during checkout. If disabled, orders default to campus pickup.
                        </p>
                      </div>
                    </div>
                    <Form.Check
                      type="switch"
                      id="enable-home-delivery-switch"
                      checked={settings.enableHomeDelivery}
                      onChange={() => handleToggle("enableHomeDelivery")}
                    />
                  </div>
                </div>
              </Col>

              {settings.enableHomeDelivery && (
                <>
                  <Col xs={12} md={6}>
                    <div className="p-3 bg-light rounded-3 border">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <Badge bg="primary" className="rounded-circle p-1">1</Badge>
                        <h6 className="fw-bold text-dark mb-0">First Bundle Delivery Fee</h6>
                      </div>
                      <p className="text-muted small mb-2">Applied as base courier charge when ordering 1 uniform bundle.</p>
                      <div className="input-group input-group-sm" style={{ maxWidth: "220px" }}>
                        <span className="input-group-text bg-white fw-bold text-dark">₹</span>
                        <Form.Control
                          type="number"
                          min={0}
                          className="fw-bold"
                          value={settings.homeDeliveryPrice}
                          onChange={(e) => updateSetting("homeDeliveryPrice", Math.max(0, parseFloat(e.target.value) || 0))}
                        />
                      </div>
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="p-3 bg-light rounded-3 border">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <Badge bg="secondary" className="rounded-circle p-1">+</Badge>
                        <h6 className="fw-bold text-dark mb-0">Additional Bundle Delivery Fee</h6>
                      </div>
                      <p className="text-muted small mb-2">Incremental charge added for each subsequent bundle (e.g. 2nd, 3rd child).</p>
                      <div className="input-group input-group-sm" style={{ maxWidth: "220px" }}>
                        <span className="input-group-text bg-white fw-bold text-dark">₹</span>
                        <Form.Control
                          type="number"
                          min={0}
                          className="fw-bold"
                          value={settings.additionalBundlePrice}
                          onChange={(e) => updateSetting("additionalBundlePrice", Math.max(0, parseFloat(e.target.value) || 0))}
                        />
                      </div>
                    </div>
                  </Col>
                </>
              )}
            </Row>

            {/* Courier Weight Slabs */}
            {settings.enableHomeDelivery && (
              <div className="border rounded-3 p-3 bg-white">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <div>
                    <h6 className="fw-bold text-dark mb-0">Courier Weight Slabs (Single Items / Non-Bundle Carts)</h6>
                    <span className="text-muted small">
                      Used to calculate shipping fee based on total weight (kg) when parents order individual loose products.
                    </span>
                  </div>
                  <div className="d-flex gap-2 align-items-center flex-wrap">
                    <span className="small text-muted fw-semibold">Presets:</span>
                    <button
                      type="button"
                      className="slab-preset-btn"
                      onClick={() => handleApplyPreset("standard")}
                    >
                      Standard 4-Tier
                    </button>
                    <button
                      type="button"
                      className="slab-preset-btn"
                      onClick={() => handleApplyPreset("fine")}
                    >
                      0.5kg Fine-Grained
                    </button>
                    <button
                      type="button"
                      className="slab-preset-btn"
                      onClick={() => handleApplyPreset("flat")}
                    >
                      Flat Rate (5kg)
                    </button>

                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="d-inline-flex align-items-center gap-1 rounded-pill fw-semibold ms-2"
                      onClick={handleAddSlab}
                    >
                      <Plus size={15} /> Add Slab
                    </Button>
                  </div>
                </div>

                {/* Slab Health Validation Alert */}
                <div className={`p-2 mb-3 rounded-2 small d-flex align-items-center gap-2 ${slabsValidation.isValid ? "bg-success bg-opacity-10 text-success" : "bg-danger bg-opacity-10 text-danger"}`}>
                  {slabsValidation.isValid ? <CheckCircle size={15} /> : <AlertTriangle size={15} />}
                  <span className="fw-semibold">{slabsValidation.message}</span>
                </div>

                <div className="table-responsive mb-3 border rounded-3 overflow-hidden">
                  <table className="settings-slabs-table">
                    <thead>
                      <tr>
                        <th style={{ width: "25%" }}>From Weight (KG)</th>
                        <th style={{ width: "25%" }}>To Weight (KG)</th>
                        <th style={{ width: "30%" }}>Courier Charge (₹)</th>
                        <th className="text-center" style={{ width: "20%" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {settings.weightSlabs && settings.weightSlabs.length > 0 ? (
                        settings.weightSlabs.map((slab, index) => (
                          <tr key={index}>
                            <td>
                              <Form.Control
                                type="number"
                                step="0.1"
                                min={0}
                                size="sm"
                                className="fw-bold text-center"
                                value={slab.minWeight}
                                onChange={(e) => handleSlabChange(index, "minWeight", e.target.value)}
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                step="0.1"
                                min={0}
                                size="sm"
                                className="fw-bold text-center"
                                value={slab.maxWeight}
                                onChange={(e) => handleSlabChange(index, "maxWeight", e.target.value)}
                              />
                            </td>
                            <td>
                              <div className="input-group input-group-sm">
                                <span className="input-group-text bg-light fw-bold text-dark border-end-0">₹</span>
                                <Form.Control
                                type="number"
                                min={0}
                                size="sm"
                                className="fw-bold text-center border-start-0"
                                value={slab.cost}
                                onChange={(e) => handleSlabChange(index, "cost", e.target.value)}
                              />
                            </div>
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="rounded-circle p-1 d-inline-flex align-items-center justify-content-center"
                              style={{ width: "30px", height: "30px" }}
                              onClick={() => handleRemoveSlab(index)}
                              disabled={settings.weightSlabs.length <= 1}
                              title="Delete slab"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-muted text-center py-3">
                          No weight slabs defined. Click "Add Slab" to configure.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Overweight Rate */}
              <Row className="g-3 align-items-center mb-3 pt-2 border-top">
                <Col xs={12} md={7}>
                  <h6 className="fw-bold text-dark small mb-1">Max Alloowed</h6>
                </Col>
                <Col xs={12} md={5}>
                  <div className="input-group input-group-sm" style={{ maxWidth: "220px" }}>
                    <span className="input-group-text bg-white fw-bold text-dark">₹</span>
                    <Form.Control
                      type="number"
                      min={0}
                      className="fw-bold text-center"
                      value={settings.maxWeight}
                      onChange={(e) => updateSetting("maxWeight", Math.max(0, parseFloat(e.target.value) || 0))}
                    />
                    <span className="input-group-text bg-light text-muted small">/ Max kgs</span>
                  </div>
                </Col>
              </Row>
              {/* <Row className="g-3 align-items-center mb-3 pt-2 border-top">
                <Col xs={12} md={7}>
                  <h6 className="fw-bold text-dark small mb-1">Overweight Per-KG Rate (Above Maximum Slab)</h6>
                  <p className="text-muted small mb-0">
                    Additional cost added per kilogram when cart package weight exceeds the highest configured slab.
                  </p>
                </Col>
                <Col xs={12} md={5}>
                  <div className="input-group input-group-sm" style={{ maxWidth: "220px" }}>
                    <span className="input-group-text bg-white fw-bold text-dark">₹</span>
                    <Form.Control
                      type="number"
                      min={0}
                      className="fw-bold text-center"
                      value={settings.perKgRate}
                      onChange={(e) => updateSetting("perKgRate", Math.max(0, parseFloat(e.target.value) || 0))}
                    />
                    <span className="input-group-text bg-light text-muted small">/ kg extra</span>
                  </div>
                </Col>
              </Row> */}

              {/* Interactive Live Shipping Calculator */}
              <div className="calc-preview-card mt-3">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <Calculator size={20} className="text-success" />
                    <div>
                      <div className="fw-bold text-dark small">Live Shipping Cost Calculator (Test Tool)</div>
                      <div className="text-muted extra-small" style={{ fontSize: "0.8rem" }}>
                        Verify your configured slabs with a test package weight.
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                      <span className="small text-muted fw-semibold">Test Weight:</span>
                      <div className="input-group input-group-sm" style={{ width: "120px" }}>
                        <Form.Control
                          type="number"
                          step="0.1"
                          min={0.1}
                          className="fw-bold text-center"
                          value={calcWeight}
                          onChange={(e) => setCalcWeight(parseFloat(e.target.value) || 0)}
                        />
                        <span className="input-group-text bg-light text-muted">kg</span>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-2 bg-white px-3 py-1 rounded-3 border border-success">
                      <span className="small text-muted fw-semibold">Calculated Shipping:</span>
                      <span className="fw-bold text-success fs-6">₹{liveShippingCost.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Section: DTDC Customer Codes & Credentials (Database Table) */}
      {(activeTab === "all" || activeTab === "dtdc") && (
        <div className="settings-section-card">
          <div className="settings-section-header">
            <h5 className="settings-section-title">
              <Truck size={20} className="text-primary" /> DTDC Customer Codes & Credentials (Database Table)
            </h5>
            <Button
              variant="primary"
              size="sm"
              className="fw-bold d-flex align-items-center gap-1 shadow-sm"
              onClick={() => setShowDtdcManager(true)}
            >
              <Plus size={14} /> Add / Manage Customer Codes
            </Button>
          </div>
          <div className="settings-section-body">
            <p className="text-muted small mb-3">
              Configure and manage multiple DTDC Customer Codes, API keys, Base URLs, and Tracking Tokens dynamically stored in the database instead of reading from <code>.env</code>.
            </p>

            {dtdcConfigs.length === 0 ? (
              <Alert variant="info" className="d-flex align-items-center justify-content-between p-3 rounded-3 mb-0">
                <div>
                  <strong>No customer codes stored in database table yet.</strong>
                  <div className="small text-muted mt-1">
                    The backend is currently using fallback credentials from your <code>.env</code> file. Click the button to add customer codes into the table.
                  </div>
                </div>
                <Button variant="outline-primary" size="sm" onClick={() => setShowDtdcManager(true)}>
                  Configure DTDC
                </Button>
              </Alert>
            ) : (
              <div className="table-responsive bg-white rounded border">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light small">
                    <tr>
                      <th>Account Name</th>
                      <th>Customer Code</th>
                      <th>Base URL / Tracking URL</th>
                      <th className="text-center">Status</th>
                      <th className="text-center">Default Account</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="small">
                    {dtdcConfigs.map((cfg) => (
                      <tr key={cfg.id}>
                        <td className="fw-semibold text-dark">
                          {cfg.accountName || `Account #${cfg.id}`}
                        </td>
                        <td>
                          <Badge bg="dark" className="font-monospace px-2 py-1">
                            {cfg.customerCode}
                          </Badge>
                        </td>
                        <td style={{ maxWidth: "250px" }}>
                          <div className="text-truncate" title={cfg.dtdcBaseUrl}>
                            <span className="text-muted small">Base: </span>
                            <small className="font-monospace">{cfg.dtdcBaseUrl}</small>
                          </div>
                          <div className="text-truncate" title={cfg.dtdcTrackingUrl}>
                            <span className="text-muted small">Trk: </span>
                            <small className="font-monospace">{cfg.dtdcTrackingUrl}</small>
                          </div>
                        </td>
                        <td className="text-center">
                          {cfg.isActive ? (
                            <Badge bg="success">Active</Badge>
                          ) : (
                            <Badge bg="secondary">Inactive</Badge>
                          )}
                        </td>
                        <td className="text-center">
                          {cfg.isDefault ? (
                            <Badge bg="primary">
                              <Check size={12} className="me-1" /> Default
                            </Badge>
                          ) : (
                            <span className="text-muted small">-</span>
                          )}
                        </td>
                        <td className="text-end">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="py-0 px-2"
                            onClick={() => setShowDtdcManager(true)}
                          >
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section 4: System Maintenance (Super Admin Only) */}
      {user?.role === ROLES.SUPER_ADMIN && (activeTab === "all" || activeTab === "maint") && (
        <div className="settings-section-card">
          <div className="settings-section-header">
            <h5 className="settings-section-title">
              <Activity size={20} className="text-danger" /> System Maintenance & Background Services
            </h5>
            <Badge bg="danger" className="px-2 py-1">
              Super Admin
            </Badge>
          </div>
          <div className="settings-section-body">
            <Row className="g-3">
              <Col xs={12} md={6}>
                <div className="maint-action-card">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <Clock size={18} className="text-primary" />
                      <h6 className="fw-bold text-dark mb-0">DTDC Courier Tracking Sync</h6>
                    </div>
                    <p className="text-muted small mb-3">
                      Synchronize latest consignment statuses, delivery confirmations, and waybill scans from DTDC API for all in-transit parcels.
                    </p>
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="w-100 fw-bold rounded-pill d-inline-flex align-items-center justify-content-center gap-2 py-2"
                    onClick={handleTriggerCron}
                    disabled={syncingTracking}
                  >
                    {syncingTracking ? <Spinner size="sm" animation="border" /> : <Activity size={16} />}
                    {syncingTracking ? "Syncing DTDC Tracking..." : "Run DTDC Courier Sync"}
                  </Button>
                </div>
              </Col>

              <Col xs={12} md={6}>
                <div className="maint-action-card">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <CreditCard size={18} className="text-success" />
                      <h6 className="fw-bold text-dark mb-0">Payment Gateway Reconciliation</h6>
                    </div>
                    <p className="text-muted small mb-3">
                      Query and reconcile pending payment transactions with payment gateway to update order statuses and capture completed checkouts.
                    </p>
                  </div>
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="w-100 fw-bold rounded-pill d-inline-flex align-items-center justify-content-center gap-2 py-2"
                    onClick={handleTransactionsStatusCheck}
                    disabled={syncingPayments}
                  >
                    {syncingPayments ? <Spinner size="sm" animation="border" /> : <CheckCircle size={16} />}
                    {syncingPayments ? "Checking Payment Statuses..." : "Reconcile Payment Transactions"}
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      )}

      {/* Security Verification Modal */}
      <Modal
        show={showSecurityModal}
        onHide={() => {
          setShowSecurityModal(false);
          setSecurityCode("");
        }}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="border-bottom pb-3">
          <Modal.Title className="h5 fw-bold d-flex align-items-center gap-2 text-dark">
            <Lock size={20} className="text-primary" />
            Security Code Verification
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSecurityCodeSubmit}>
          <Modal.Body className="py-4">
            <p className="text-muted small mb-3">
              You are updating critical global store rules and logistics pricing. Please enter your administrator security code to authorize this change.
            </p>

            <Form.Group className="mb-2">
              <Form.Label className="small fw-bold text-uppercase">Security Code / PIN</Form.Label>
              <div className="input-group">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter Security Code..."
                  className="fw-bold"
                  value={securityCode}
                  onChange={(e) => setSecurityCode(e.target.value)}
                  autoFocus
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </div>
            </Form.Group>
          </Modal.Body>

          <Modal.Footer className="border-top pt-3">
            <Button
              variant="outline-secondary"
              onClick={() => {
                setShowSecurityModal(false);
                setSecurityCode("");
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={loading || !securityCode.trim()}
              className="fw-semibold px-4 d-inline-flex align-items-center gap-2 shadow-sm"
            >
              {loading ? <Spinner size="sm" animation="border" /> : <ShieldCheck size={16} />}
              Authorize & Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* DTDC Configuration Manager Modal */}
      <DtdcConfigManager
        show={showDtdcManager}
        onHide={() => setShowDtdcManager(false)}
        onConfigChange={(list) => {
          setDtdcConfigs(Array.isArray(list) ? list : []);
        }}
      />
    </div>
  );
};

export default Settings;